import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bookmark } from "lucide-react";

import ViewContainer from "../../../../../components/layout/view/ViewContainer";

import ContentPanel from "../../../../../components/layout/content/ContentPanel";
import ContentPanelHeader from "../../../../../components/layout/content/ContentPanelHeader";

import ContentGrid from "../../../../../components/grids/content/ContentGrid";
import EmptyState from "../../../../../components/feedback/EmptyState";
import Button from "../../../../../components/buttons/Button";
import PopThumbnailCard from "../../../../../components/cards/pops/PopThumbnailCard";

import { useUserSavedPops } from "../hooks/useUserSavedPops";
import { useUserPopsViews } from "../hooks/useUserPopsViews";

import "./UserSavedPopsView.css";

const LIMIT = 12;
const VIEW_BATCH_SIZE = 50;

const getErrorMessage = (error) =>
  typeof error === "string"
    ? error
    : error?.response?.data?.message ||
      error?.message ||
      "Unable to load saved Pops.";

const normalizeCount = (value, fallback = 0) => {
  if (value === null || value === undefined || value === "") {
    return fallback;
  }

  const count = Number(value);

  return Number.isFinite(count) && count >= 0 ? count : fallback;
};

export default function UserSavedPopsView() {
  const navigate = useNavigate();

  const {
    savedPops = [],
    loading,
    loadingMore,
    error,
    hasMore,
    total,
    loadMore,
    refresh,
  } = useUserSavedPops({
    limit: LIMIT,
    autoLoad: true,
  });

  const { getPopViewCounts } = useUserPopsViews();

  const [viewCounts, setViewCounts] = useState({});
  const requestedIds = useRef(new Set());
  const countRequest = useRef(getPopViewCounts);

  useEffect(() => {
    countRequest.current = getPopViewCounts;
  }, [getPopViewCounts]);

  const pops = useMemo(() => {
    const seen = new Set();

    return (Array.isArray(savedPops) ? savedPops : []).filter((pop) => {
      const id = Number(pop?.popId);

      if (!Number.isSafeInteger(id) || id <= 0 || seen.has(id)) {
        return false;
      }

      seen.add(id);
      return true;
    });
  }, [savedPops]);

  const popIds = useMemo(() => pops.map((pop) => Number(pop.popId)), [pops]);

  useEffect(() => {
    let cancelled = false;

    const missingIds = popIds.filter((id) => !requestedIds.current.has(id));

    if (!missingIds.length) return;

    missingIds.forEach((id) => requestedIds.current.add(id));

    const loadCounts = async () => {
      for (let i = 0; i < missingIds.length; i += VIEW_BATCH_SIZE) {
        if (cancelled) break;

        const ids = missingIds.slice(i, i + VIEW_BATCH_SIZE);

        try {
          const results = await countRequest.current(ids);

          if (cancelled) return;

          if (!Array.isArray(results)) {
            throw new Error("Invalid Pop view-count response.");
          }

          const counts = {};

          results.forEach((item) => {
            const id = Number(item?.popId ?? item?.pop_id);

            if (!Number.isSafeInteger(id) || id <= 0) return;

            const realViews = normalizeCount(
              item.realViews ?? item.real_views ?? item.views,
            );

            const demoViews = normalizeCount(item.demoViews ?? item.demo_views);

            counts[id] = {
              realViews,
              demoViews,
              totalViews: normalizeCount(
                item.totalViews ?? item.total_views,
                realViews + demoViews,
              ),
            };
          });

          setViewCounts((previous) => ({
            ...previous,
            ...counts,
          }));

          ids
            .filter((id) => !counts[id])
            .forEach((id) => requestedIds.current.delete(id));
        } catch (error) {
          if (!cancelled) {
            console.error("Failed to load Pop views:", error);
          }

          ids.forEach((id) => requestedIds.current.delete(id));
        }
      }
    };

    void loadCounts();

    return () => {
      cancelled = true;

      missingIds.forEach((id) => requestedIds.current.delete(id));
    };
  }, [popIds]);

  const displayPops = useMemo(
    () =>
      pops.map((pop) => {
        const realViews = normalizeCount(
          pop.realViews ?? pop.real_views ?? pop.views,
        );

        const demoViews = normalizeCount(pop.demoViews ?? pop.demo_views);

        const counts = viewCounts[pop.popId];

        const totalViews = counts
          ? counts.totalViews
          : normalizeCount(
              pop.totalViews ?? pop.total_views,
              realViews + demoViews,
            );

        return {
          ...pop,
          realViews: counts?.realViews ?? realViews,
          demoViews: counts?.demoViews ?? demoViews,
          totalViews,
          views: totalViews,
          isSaved: true,
        };
      }),
    [pops, viewCounts],
  );

  const savedCount =
    Number.isFinite(Number(total)) && Number(total) > 0
      ? Number(total)
      : pops.length;

  const handleRefresh = async () => {
    if (loading || loadingMore) return;

    try {
      await refresh();
    } catch (error) {
      console.error("Failed to refresh saved Pops:", error);
    }
  };

  const handleLoadMore = async () => {
    if (loading || loadingMore || !hasMore) return;

    try {
      await loadMore();
    } catch (error) {
      console.error("Failed to load more saved Pops:", error);
    }
  };

  const renderPop = (pop) => (
    <PopThumbnailCard
      key={pop.popId ?? pop.popTag}
      pop={pop}
      mode="grid"
      onClick={() => navigate(`/pops/${pop.popId}`)}
    />
  );

  const errorMessage = error ? getErrorMessage(error) : "";
  const hasPops = displayPops.length > 0;

  return (
    <ViewContainer>
      <ContentPanel>
        <ContentPanelHeader
          title={`Saved Pops (${savedCount})`}
          subTitle="Your collection of saved Pops."
        />

        {errorMessage && hasPops && (
          <div className="user-saved-pops__error" role="alert">
            <p>{errorMessage}</p>

            <Button
              value="Try Again"
              variant="primary-outlined"
              onClick={handleRefresh}
              disabled={loading || loadingMore}
            />
          </div>
        )}

        {loading && !hasPops ? (
          <ContentGrid
            items={[]}
            loading
            columns={6}
            tabletColumns={3}
            mobileColumns={2}
            gap={16}
            skeletonCount={LIMIT}
            showEmpty={false}
            renderItem={() => null}
          />
        ) : !hasPops ? (
          <EmptyState
            icon={<Bookmark size={28} />}
            title={errorMessage ? "Unable to load saved Pops" : "No saved Pops"}
            description={errorMessage || "Pops you save will appear here."}
            action={
              errorMessage ? (
                <Button
                  value="Try Again"
                  variant="primary-outlined"
                  onClick={handleRefresh}
                  disabled={loading || loadingMore}
                />
              ) : null
            }
          />
        ) : (
          <ContentGrid
            items={displayPops}
            columns={6}
            tabletColumns={3}
            mobileColumns={2}
            gap={16}
            showEmpty={false}
            renderItem={renderPop}
          />
        )}

        {hasMore && hasPops && (
          <div className="user-saved-pops__footer">
            <Button
              value={loadingMore ? "Loading..." : "Load More"}
              variant="primary-outlined"
              onClick={handleLoadMore}
              disabled={loading || loadingMore}
            />
          </div>
        )}
      </ContentPanel>
    </ViewContainer>
  );
}
